import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  Verified as ActiveIcon,
  Edit as EditIcon,
  Store as VendorIcon,
} from "@mui/icons-material";
import { type TaskerTask } from "../../../services/taskerService";
import { usePriorSteps } from "./priorSteps";
import { VendorApplicationSummary } from "./VendorApplicationSummary";

const SUCCESS_BG = "#E6F4EA";
const SUCCESS_BORDER = "#B7E4C7";
const SUCCESS_DARK = "#0F5132";

type Props = {
  task: TaskerTask;
  vendorLegalName?: string;
};

export function ActivatedPanel({ task, vendorLegalName }: Props) {
  const priorStepsQuery = usePriorSteps(task.id, task.task_type_id);
  const priorSteps = priorStepsQuery.data;

  const legalName = (() => {
    const s1 = priorSteps?.find(
      (s) => s.metadata?.step_name === "business_details",
    );
    return typeof s1?.data.vendor_legal_name === "string"
      ? (s1.data.vendor_legal_name as string)
      : undefined;
  })();

  if (priorStepsQuery.isError) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="warning">
            Vendor is active, but the submitted profile couldn&apos;t be
            loaded. Refresh to retry.
          </Alert>
        </Box>
      </Box>
    );
  }

  if (priorStepsQuery.isPending) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const displayName = legalName || vendorLegalName || "Vendor";
  const vendorCode = task.task_visible_id ?? "—";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
      <Box sx={{ maxWidth: 1120, mx: "auto", px: 3, py: 3 }}>
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "#E0E4EF",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              px: 4,
              py: 3,
              borderBottom: 1,
              borderColor: "#E0E4EF",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: "primary.light",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VendorIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                {vendorCode} · {displayName}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Vendor Profile · VIP · {displayName}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            <Paper
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2.5,
                borderColor: SUCCESS_BORDER,
                bgcolor: SUCCESS_BG,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: SUCCESS_DARK,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ActiveIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: SUCCESS_DARK }}>
                  Vendor active in VIP
                </Typography>
                <Typography variant="body2" sx={{ color: SUCCESS_DARK, mt: 0.25 }}>
                  Vendor Code {vendorCode} · profile live across VMS, VIP, ERP
                </Typography>
              </Box>
              <Chip
                label="ACTIVE"
                size="small"
                sx={{
                  bgcolor: SUCCESS_DARK,
                  color: "#fff",
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  fontSize: 11,
                }}
              />
            </Paper>
          </Box>

          <Box sx={{ px: 3, pb: 3 }}>
            <VendorApplicationSummary
              priorSteps={priorSteps ?? []}
              authMode="reviewer"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 4,
              py: 3,
              borderTop: 1,
              borderColor: "#E0E4EF",
              bgcolor: "#FAFBFF",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                Post-onboarding edits
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                Vendors request changes; governance routes each field via Path A
                / B / C.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              disabled
              sx={{ borderColor: "#E0E4EF", color: "text.secondary" }}
            >
              Initiate edit request
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ActivatedPanel;
